import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { formatMoney } from '../lib/currency';
import {
  Sparkles, MapPin, Star, BookOpen, CheckCircle2,
  Globe, Mail, Phone, ExternalLink, Clock,
  ArrowRight, ChevronLeft
} from 'lucide-react';

const categoryLabels = {
  professional_training: 'Professional Training',
  tech_bootcamp: 'Tech Bootcamp',
  language: 'Language',
  creative: 'Creative',
  wellness: 'Wellness',
};

const levelColors = {
  beginner: 'bg-emerald-50 text-emerald-700',
  intermediate: 'bg-amber-50 text-amber-700',
  advanced: 'bg-rose-50 text-rose-700',
};

function StarRating({ rating, size = 'normal' }) {
  const sz = size === 'small' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${sz} ${s <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`}
        />
      ))}
    </div>
  );
}

function StarSelector({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
        >
          <Star
            className={`w-6 h-6 cursor-pointer transition-colors ${
              s <= (hovered || value) ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function CourseProviderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviewForm, setReviewForm] = useState({ rating: 0, review: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProvider = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/course-providers/${id}`);
      setProvider(res.data);
    } catch {
      setProvider(null);
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (reviewForm.rating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmittingReview(true);
    try {
      await api.post(`/course-providers/${id}/rate`, reviewForm);
      toast.success('Review submitted successfully!');
      setReviewForm({ rating: 0, review: '' });
      fetchProvider();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'courses', label: 'Courses' },
    { id: 'reviews', label: 'Reviews' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8]">
        <div className="h-48 bg-gradient-to-r from-purple-700 to-fuchsia-700" />
        <div className="max-w-4xl mx-auto px-4 -mt-12 pb-12">
          <Skeleton className="h-24 w-24 rounded-full mb-4" />
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-5 w-40" />
        </div>
      </div>
    );
  }

  if (!provider) return null;

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Cover Banner */}
      <div className="bg-gradient-to-r from-purple-700 to-fuchsia-700 pt-6 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-white/70 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Course Providers
          </button>
        </div>
      </div>

      {/* Profile Section */}
      <div className="max-w-4xl mx-auto px-4 -mt-12 mb-8">
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-5 items-start">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-100 to-fuchsia-200 flex items-center justify-center shadow-md shrink-0">
                <Sparkles className="w-10 h-10 text-purple-700" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start gap-2 mb-2">
                  <h1 className="text-2xl font-bold text-foreground">{provider.business_name}</h1>
                  {provider.is_verified && (
                    <span className="flex items-center gap-1 bg-purple-50 text-purple-700 text-xs font-medium px-2 py-1 rounded-full">
                      <CheckCircle2 className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge className="bg-purple-100 text-purple-700">
                    {categoryLabels[provider.category] || provider.category}
                  </Badge>
                  {provider.location && (
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" />
                      {provider.location}
                    </span>
                  )}
                </div>
                {provider.average_rating > 0 && (
                  <div className="flex items-center gap-2">
                    <StarRating rating={provider.average_rating} />
                    <span className="text-sm font-medium">{provider.average_rating?.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Bar */}
      <div className="max-w-4xl mx-auto px-4 mb-6">
        <div className="flex gap-1 bg-white rounded-xl p-1.5 shadow-sm border w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-700 text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>About</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">{provider.about}</p>
                  {(provider.expertise || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {provider.expertise.map((e, i) => (
                        <Badge key={i} variant="outline">{e}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-2">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-xl font-bold text-foreground">{(provider.courses_count || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">Courses</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
                      <Star className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-xl font-bold text-foreground">{provider.average_rating?.toFixed(1) || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Avg Rating</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {provider.website && (
                    <a
                      href={provider.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-purple-700 hover:underline"
                    >
                      <Globe className="w-4 h-4 shrink-0" />
                      <span className="truncate">{provider.website.replace(/^https?:\/\//, '')}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  )}
                  {provider.email && (
                    <a
                      href={`mailto:${provider.email}`}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                    >
                      <Mail className="w-4 h-4 shrink-0" />
                      {provider.email}
                    </a>
                  )}
                  {provider.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4 shrink-0" />
                      {provider.phone}
                    </div>
                  )}
                  {provider.location && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 shrink-0" />
                      {provider.location}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">
              Available Courses ({(provider.courses || []).length})
            </h2>
            {(provider.courses || []).length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No courses listed yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(provider.courses || []).map((course) => (
                  <Card key={course.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-foreground flex-1 pr-2">{course.title}</h3>
                        {course.is_free ? (
                          <Badge className="bg-emerald-100 text-emerald-700 shrink-0">Free</Badge>
                        ) : (
                          <span className="text-sm font-bold text-purple-700 shrink-0">
                            {formatMoney(course.price, course.currency)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {course.level && (
                          <Badge className={`text-xs ${levelColors[course.level] || 'bg-gray-100 text-gray-700'}`}>
                            {course.level.charAt(0).toUpperCase() + course.level.slice(1)}
                          </Badge>
                        )}
                        {course.duration && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {course.duration}
                          </span>
                        )}
                      </div>
                      <Link to={`/courses/${course.id}`}>
                        <Button size="sm" className="w-full bg-purple-700 hover:bg-purple-800">
                          View Course
                          <ArrowRight className="w-3.5 h-3.5 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                Reviews ({(provider.ratings || []).length})
              </h2>
              {provider.average_rating > 0 && (
                <div className="flex items-center gap-2">
                  <StarRating rating={provider.average_rating} />
                  <span className="font-bold">{provider.average_rating?.toFixed(1)}</span>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {(provider.ratings || []).length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
                  </CardContent>
                </Card>
              ) : (
                (provider.ratings || []).map((review) => (
                  <Card key={review.id}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-sm">{review.user_name || 'Anonymous'}</p>
                          <p className="text-xs text-muted-foreground">
                            {review.created_at?.slice(0, 10)}
                          </p>
                        </div>
                        <StarRating rating={review.rating} size="small" />
                      </div>
                      {review.review && (
                        <p className="text-sm text-muted-foreground">{review.review}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {isAuthenticated ? (
              <Card className="border-2 border-purple-100">
                <CardHeader>
                  <CardTitle className="text-base">Write a Review</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submitReview} className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Your Rating</p>
                      <StarSelector
                        value={reviewForm.rating}
                        onChange={(val) => setReviewForm({ ...reviewForm, rating: val })}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Your Review</p>
                      <textarea
                        className="w-full border rounded-lg p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
                        rows={4}
                        placeholder="Share your experience with this trainer..."
                        value={reviewForm.review}
                        onChange={(e) => setReviewForm({ ...reviewForm, review: e.target.value })}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submittingReview || reviewForm.rating === 0}
                      className="bg-purple-700 hover:bg-purple-800"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-purple-50 border-purple-100">
                <CardContent className="p-5 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    Sign in to write a review
                  </p>
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="border-purple-700 text-purple-700">
                      Sign In to Review
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
